import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Umbrella, Loader2, CalendarRange, AlertCircle, Clock,
  FileText, ChevronRight, Users, CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const COLOUR_MAP = {
  annual: "bg-blue-100 text-blue-700 border-blue-200",
  sick: "bg-red-100 text-red-700 border-red-200",
  study: "bg-purple-100 text-purple-700 border-purple-200",
  family: "bg-amber-100 text-amber-700 border-amber-200",
  responsibility: "bg-orange-100 text-orange-700 border-orange-200",
  long_service: "bg-teal-100 text-teal-700 border-teal-200",
  unpaid: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function LMSDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/lms/dashboard-stats`, {
        headers: getHeaders(), withCredentials: true,
      });
      setStats(res.data);
    } catch { toast.error("Failed to load dashboard"); }
    setLoading(false);
  };

  if (loading) {
    return <div className="p-6 flex items-center justify-center min-h-[400px]">
      <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
    </div>;
  }

  if (!stats) {
    return <div className="p-6 text-center text-slate-400 text-sm">Unable to load dashboard data.</div>;
  }

  const { member, balances, application_counts, pending_approvals, team_stats } = stats;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-slate-800">Leave Dashboard</h2>
        <p className="text-xs text-slate-400">{member?.name} &middot; {member?.department || ""}</p>
      </div>

      {/* Balance Cards */}
      <div>
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Leave Balances</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
          {balances?.map((b) => (
            <div key={b.leave_type_id} className="bg-white border border-slate-200 p-3">
              <div className="flex items-center gap-1.5 mb-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: b.colour }} />
                <span className="text-[10px] font-medium text-slate-500 uppercase">{b.leave_type_code}</span>
              </div>
              <p className="text-2xl font-bold text-slate-800">{b.remaining.toFixed(1)}</p>
              <div className="text-[9px] text-slate-400 mt-1 space-y-0.5">
                <span>Entitled: {b.total_entitled.toFixed(1)}</span>
                {b.taken > 0 && <span> &middot; Taken: {b.taken.toFixed(1)}</span>}
                {b.pending > 0 && <span> &middot; Pending: {b.pending.toFixed(1)}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions + Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Quick Actions */}
        <div className="bg-white border border-slate-200 p-4 space-y-3">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Quick Actions</h3>
          <Button onClick={() => navigate("/hr-hub/leave-management/request")}
            className="w-full h-9 text-xs rounded-none bg-emerald-600 hover:bg-emerald-500 justify-between">
            Apply for Leave <ChevronRight className="w-3 h-3" />
          </Button>
          <Button onClick={() => navigate("/hr-hub/leave-management/calendar")}
            variant="outline" className="w-full h-9 text-xs rounded-none justify-between">
            View Leave Calendar <CalendarRange className="w-3 h-3" />
          </Button>
          <Button onClick={() => navigate("/hr-hub/leave-management/my-leave")}
            variant="outline" className="w-full h-9 text-xs rounded-none justify-between">
            My Applications <FileText className="w-3 h-3" />
          </Button>
        </div>

        {/* Application Summary */}
        <div className="bg-white border border-slate-200 p-4 space-y-3">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">My Applications</h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-600">Total</span>
              <span className="font-semibold text-slate-800">{application_counts?.total || 0}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-amber-600 flex items-center gap-1"><Clock className="w-3 h-3" /> Pending</span>
              <span className="font-semibold">{application_counts?.pending || 0}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Approved</span>
              <span className="font-semibold">{application_counts?.approved || 0}</span>
            </div>
          </div>
        </div>

        {/* Team Stats (if manager) */}
        {team_stats && (
          <div className="bg-white border border-slate-200 p-4 space-y-3">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Team Overview</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-600 flex items-center gap-1"><Users className="w-3 h-3" /> Members</span>
                <span className="font-semibold">{team_stats.total_members}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-amber-600">On leave today</span>
                <span className="font-semibold">{team_stats.on_leave_today}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-emerald-600">Capacity</span>
                <span className="font-semibold">{team_stats.capacity_pct}%</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-red-600 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Pending approvals</span>
                <span className="font-semibold">{team_stats.pending_approvals_count}</span>
              </div>
              {team_stats.pending_approvals_count > 0 && (
                <Button onClick={() => navigate("/hr-hub/leave-management/approvals")}
                  size="sm" className="w-full h-7 text-[10px] rounded-none bg-emerald-600 mt-2">
                  Review Approvals
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Pending Approvals */}
      {pending_approvals?.length > 0 && (
        <div className="bg-white border border-slate-200">
          <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Recent Pending Approvals</h3>
            <Button variant="ghost" size="sm" onClick={() => navigate("/hr-hub/leave-management/approvals")}
              className="h-6 text-[10px]">
              View All
            </Button>
          </div>
          <div className="divide-y divide-slate-100">
            {pending_approvals.map((app) => (
              <div key={app.id} className="px-4 py-2.5 flex items-center justify-between hover:bg-app">
                <div>
                  <p className="text-xs font-medium text-slate-800">{app.employee_name}</p>
                  <p className="text-[10px] text-slate-400">
                    {app.leave_type_name} &middot; {app.total_days} days &middot; {app.start_date} to {app.end_date}
                  </p>
                </div>
                <Badge className="text-[9px] px-1.5 py-0 bg-amber-100 text-amber-700 border-none">
                  Pending
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
